import { fmt } from "./format";
import { CASE_TYPES, TERMINATION_OPTIONS } from "../data/constants";

const persianDigits = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const toFa = (value) =>
  String(value).replace(/[0-9]/g, (d) => persianDigits[+d]);

// تاریخ و ساعت شمسی جاری، دقیقاً به فرمت «پنجشنبه ۱۴۰۵/۰۵/۰۸ ساعت ۱۲:۰۲:۳۴»
function getPersianNow() {
  const parts = new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
    weekday: "long",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date());

  const map = {};
  parts.forEach((p) => (map[p.type] = p.value));

  return `${map.weekday} ${map.year}/${map.month}/${map.day} ساعت ${map.hour}:${map.minute}:${map.second}`;
}

const CRIMINAL_TIER_LABELS = {
  minor: "شلاق / جزای نقدی / سرقت حدی / حبس کمتر از ۱۰ سال",
  major: "قصاص نفس / محاربه / حدود مستوجب قتل / حبس ۱۰ سال و بیشتر",
  other: "سایر دعاوی غیرمالی کیفری",
};

const ASSOCIATION_LABELS = {
  kanoon: "کانون وکلای دادگستری",
  markaz: "مرکز امور وکلا و مشاوران قوه قضائیه",
};

const EDUCATION_LABELS = {
  none: "کارشناسی / بدون افزایش",
  masters: "کارشناسی ارشد",
  phd: "دکترا",
};

const CITY_TIER_LABELS = {
  none: "سایر مناطق",
  tehran: "تهران / مراکز استان",
  other: "سایر شهرهای بزرگ",
};

function amountLine(label, rialValue, toDisplay, unitLabel) {
  return `${label} : ${toFa(fmt(toDisplay(rialValue)))} ${unitLabel}`;
}

/**
 * متن نهایی برای اشتراک‌گذاری خروجی محاسبه‌گر حق‌الوکاله را می‌سازد.
 * خروجی همان ساختار (ورودی‌ها / نتیجه محاسبه / مستندات / توضیحات / زمان محاسبه)
 * را دنبال می‌کند، اما دقیقاً منطبق بر داده‌های واقعی این کامپوننت است.
 */
export function buildLawyerFeeShareText({
  caseType,
  unit,
  unitLabel,
  result,
  toDisplay,
  financialBasis,
  claimAmount,
  agreedFee,
  termination,
  criminalTier,
  repeatCounts,
  executionAmount,
  association,
  specializedAmount,
  education,
  experience,
  cityTier,
}) {
  if (!result) return "";

  const lines = [];
  const caseTypeLabel =
    CASE_TYPES.find((c) => c.id === caseType)?.label || "";

  lines.push("محاسبه حق‌الوکاله و تمبر مالیاتی");
  lines.push("");
  lines.push("ورودی‌ها :");
  lines.push(`نوع دعوا : ${caseTypeLabel}`);

  if (caseType === "financial") {
    lines.push(
      `مبنای محاسبه : ${
        financialBasis === "agreed"
          ? "مبلغ مندرج در وکالت‌نامه (توافقی)"
          : "بهای خواسته (تعرفه پلکانی)"
      }`
    );
    if (financialBasis === "agreed") {
      lines.push(
        `مبلغ حق‌الوکاله مندرج در وکالت‌نامه : ${toFa(agreedFee || "۰")} ${unitLabel}`
      );
    } else {
      lines.push(`بهای خواسته : ${toFa(claimAmount || "۰")} ${unitLabel}`);
    }
    lines.push(
      `نحوه ختم پرونده : ${
        TERMINATION_OPTIONS.find((o) => o.id === termination)?.label || ""
      }`
    );
  } else if (caseType === "criminal") {
    lines.push(`نوع جرم : ${CRIMINAL_TIER_LABELS[criminalTier] || ""}`);
    if (criminalTier !== "other") {
      lines.push(`تعداد عناوین تعدد جرم : ${toFa(repeatCounts || 0)}`);
    }
  } else if (caseType === "execution") {
    lines.push(
      `مبلغ محکوم‌به یا مورد اجرا : ${toFa(executionAmount || "۰")} ${unitLabel}`
    );
    lines.push(`نظام صنفی وکیل : ${ASSOCIATION_LABELS[association] || ""}`);
  } else if (caseType === "specialized") {
    lines.push(
      `مابه‌الاختلاف مالی (جهت تمبر) : ${toFa(specializedAmount || "۰")} ${unitLabel}`
    );
  }

  if (caseType === "financial" || caseType === "nonFinancial" || caseType === "criminal") {
    lines.push(
      `تحصیلات وکیل : ${EDUCATION_LABELS[education] || ""} — سنوات خدمت : ${toFa(
        experience || 0
      )} سال — محل اشتغال : ${CITY_TIER_LABELS[cityTier] || ""}`
    );
  }

  lines.push("");
  lines.push("نتیجه محاسبه :");

  if (result.isRange) {
    lines.push(
      `محدوده قانونی : ${toFa(fmt(toDisplay(result.min)))} تا ${toFa(
        fmt(toDisplay(result.max))
      )} ${unitLabel}`
    );
  }

  if (result.hasStages) {
    lines.push(amountLine("حق‌الوکاله مرحله بدوی (۶۰٪)", result.stageFirst, toDisplay, unitLabel));
    lines.push(amountLine("حق‌الوکاله مرحله تجدیدنظر (۴۰٪)", result.stageAppeal, toDisplay, unitLabel));
  }

  lines.push(amountLine("جمع کل حق‌الوکاله", result.total, toDisplay, unitLabel));

  lines.push("");
  lines.push("تمبر مالیاتی :");

  if (result.isFinancialStamp) {
    lines.push("کانون وکلای دادگستری :");
    lines.push(amountLine("  تمبر مرحله بدوی", result.kanoonStampFirst, toDisplay, unitLabel));
    lines.push(amountLine("  تمبر مرحله تجدیدنظر", result.kanoonStampAppeal, toDisplay, unitLabel));
    lines.push("مرکز امور وکلا و مشاوران قوه قضائیه :");
    lines.push(amountLine("  تمبر مرحله بدوی", result.markazStampFirst, toDisplay, unitLabel));
    lines.push(amountLine("  تمبر مرحله تجدیدنظر", result.markazStampAppeal, toDisplay, unitLabel));
  } else if (result.breakdownFirst) {
    lines.push(amountLine("جمع تمبر مالیاتی", result.breakdownFirst.total, toDisplay, unitLabel));
    lines.push(
      amountLine(`سهم ${result.breakdownFirst.fundLabel}`, result.breakdownFirst.fundShare, toDisplay, unitLabel)
    );
    if (result.breakdownFirst.barShare) {
      lines.push(amountLine("  سهم کانون", result.breakdownFirst.barShare, toDisplay, unitLabel));
    }
  } else if (typeof result.stampTax === "number") {
    lines.push(amountLine("جمع تمبر مالیاتی", result.stampTax, toDisplay, unitLabel));
  }

  if (result.note) {
    lines.push("");
    lines.push("توضیحات :");
    lines.push(result.note);
  }

  lines.push("");
  lines.push(`زمان محاسبه :`);
  lines.push(getPersianNow());
  lines.push("");
  lines.push("اپلیکیشن مدیریت دفتر وکیل و هوش مصنوعی حقوقی وکیلسان");
  lines.push("https://vakilsun.com");

  return lines.join("\n");
}
